// Cross-cutting presentational helpers shared by the Community modules.
//
// These are the small pure helpers and the `Avatar` primitive that two or more
// extracted community modules use. Module-local helpers stay in their owning
// module; only the genuinely shared ones live here.
//
// No em-dashes or en-dashes anywhere (brand rule). Hyphens in compound words
// and numeric ranges are fine.

import type { api } from "@healthos/schemas";
import { compactAgeLabel } from "@healthos/shared";
import type { ReactElement, ReactNode } from "react";
import { useCommunityProfileTrigger } from "./profile-trigger-context";
import { useAuthGatedImageUrl } from "./use-auth-gated-image";

export function Avatar({
  author,
  size,
  className,
  enableProfileTrigger = true,
}: {
  author: Pick<api.CommunityAuthor, "displayName" | "initials" | "avatarColor" | "avatarUrl"> & {
    userId?: string;
  };
  size: "sm" | "smPlus" | "md" | "lg" | "xl";
  className?: string;
  // Opt out of the profile-trigger button wrapper when this avatar renders
  // inside another interactive element (a button cannot nest a button).
  enableProfileTrigger?: boolean;
}) {
  const openProfile = useCommunityProfileTrigger(author);
  const dims =
    size === "xl"
      ? "h-28 w-28 text-5xl"
      : size === "lg"
        ? "h-16 w-16 text-3xl"
        : size === "md"
          ? "h-12 w-12 text-lg"
          : size === "smPlus"
            ? "h-10 w-10 text-base"
            : "h-7 w-7 text-xs";

  const isAuthGated =
    author.avatarUrl?.startsWith("/api/") || author.avatarUrl?.startsWith("/images/");
  const imageState = useAuthGatedImageUrl(isAuthGated ? author.avatarUrl : null);

  let avatar: ReactElement;
  if (isAuthGated) {
    if (imageState.status === "loaded" && imageState.url) {
      avatar = (
        <AvatarImage
          src={imageState.url}
          displayName={author.displayName}
          className={`${dims} ${className ?? ""}`}
        />
      );
    } else if (imageState.status === "loading") {
      avatar = (
        <span
          title={author.displayName}
          className={`${dims} grid shrink-0 place-items-center overflow-hidden rounded-full bg-mist ${className ?? ""}`}
          aria-hidden="true"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-gray-300" />
        </span>
      );
    } else {
      avatar = (
        <span
          title={author.displayName}
          className={`${dims} grid shrink-0 place-items-center overflow-hidden rounded-full text-center font-sans font-semibold leading-none ${className ?? ""}`}
          style={{
            color: avatarInk(author.avatarColor),
            backgroundColor: colorWash(author.avatarColor),
          }}
          aria-hidden="true"
        >
          {author.initials}
        </span>
      );
    }
  } else if (author.avatarUrl) {
    avatar = (
      <AvatarImage
        src={author.avatarUrl}
        displayName={author.displayName}
        className={`${dims} ${className ?? ""}`}
      />
    );
  } else {
    avatar = (
      <span
        title={author.displayName}
        className={`${dims} grid shrink-0 place-items-center overflow-hidden rounded-full text-center font-sans font-semibold leading-none ${className ?? ""}`}
        style={{
          color: avatarInk(author.avatarColor),
          backgroundColor: colorWash(author.avatarColor),
        }}
        aria-hidden="true"
      >
        {author.initials}
      </span>
    );
  }

  if (!openProfile || !enableProfileTrigger) return avatar;
  return (
    <button
      type="button"
      onClick={openProfile}
      aria-label={`View ${author.displayName}'s profile`}
      className="block shrink-0 rounded-full bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
    >
      {avatar}
    </button>
  );
}

/** Avatar beside the card header only; body copy sits in FeedCardBodySection below. */
export function FeedCardAuthorHeader({
  author,
  avatarClassName,
  children,
  className,
}: {
  author: Pick<api.CommunityAuthor, "displayName" | "initials" | "avatarColor" | "avatarUrl">;
  avatarClassName?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 px-5 pt-5 pr-12 ${className ?? ""}`}>
      <Avatar author={author} size="md" className={`shrink-0 ${avatarClassName ?? ""}`} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Full-width card body below the author header row. */
export function FeedCardBodySection({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`px-5 pb-4 ${className ?? ""}`}>{children}</div>;
}

function AvatarImage({
  src,
  displayName,
  className,
}: {
  src: string;
  displayName: string;
  className: string;
}) {
  return (
    <span
      title={displayName}
      className={`grid shrink-0 aspect-square overflow-hidden rounded-full bg-mist ${className}`}
    >
      <img
        src={src}
        alt={displayName}
        loading="eager"
        decoding="async"
        className="block h-full w-full object-cover"
      />
    </span>
  );
}

// Seeded avatar colors arrive as palette words, several of which are not
// valid CSS colors; an invalid keyword voids the whole color-mix() and the
// initials render with no wash at all. Resolve the vocabulary onto DS
// tokens and fall back to teal for anything unrecognized.
const AVATAR_ACCENTS: Record<string, string> = {
  amber: "var(--color-amber-600)",
  blue: "var(--color-blue-600)",
  cream: "var(--color-day-morning)",
  cyan: "var(--color-teal-500)",
  green: "var(--color-leaf-dark)",
  lavender: "var(--color-purple-400)",
  leaf: "var(--color-leaf)",
  pink: "var(--color-rose-600)",
  rose: "var(--color-rose-600)",
  teal: "var(--color-teal-600)",
};

function avatarAccent(color: string | null | undefined): string | null {
  if (!color) return null;
  const mapped = AVATAR_ACCENTS[color.toLowerCase()];
  if (mapped) return mapped;
  if (color.startsWith("#") || color.startsWith("var(")) return color;
  return null;
}

function colorWash(color: string | null | undefined): string {
  const accent = avatarAccent(color);
  if (!accent) return "color-mix(in srgb, var(--color-teal-600) 14%, white)";
  return `color-mix(in srgb, ${accent} 16%, white)`;
}

// The accent hue arrives with arbitrary lightness; mixing it into navy keeps
// the hue identity while guaranteeing dark ink on the light wash above.
function avatarInk(color: string | null | undefined): string {
  const accent = avatarAccent(color);
  if (!accent) return "var(--color-teal-700)";
  return `color-mix(in srgb, ${accent} 40%, var(--color-navy))`;
}

export function shortName(name: string): string {
  const [first, second] = name.split(" ");
  if (!second) return first ?? name;
  return `${first} ${second.charAt(0)}.`;
}

export function formatRecipientNames(recipients: api.CommunityKudosRecipient[]): string {
  if (recipients.length === 0) return "teammate";
  if (recipients.length === 1) return recipients[0]?.displayName ?? "teammate";
  if (recipients.length === 2) {
    return `${recipients[0]?.displayName ?? "teammate"} and ${
      recipients[1]?.displayName ?? "teammate"
    }`;
  }
  return `${recipients[0]?.displayName ?? "teammate"} and ${recipients.length - 1} others`;
}

const RELATIVE_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

export function formatRelative(value: string): string {
  const then = new Date(value);
  // beyond a week an absolute date reads better than a ladder count
  if (Date.now() - then.getTime() >= 7 * 86_400_000) return RELATIVE_DATE_FORMAT.format(then);
  return compactAgeLabel(then.getTime(), Date.now());
}
